// T-039 — GDPR Art. 17 account deletion.
// Deletes the server account and all local data; leaves nothing behind.

import { getAuthService } from "./auth";
import { setOwner, deleteActiveDb } from "./db";
import { sync as syncEngine } from "./sync";

// In-flight deletion tracking. `performAccountDeletion` is async — once the
// user has confirmed the sheet the chain runs for as long as the server
// takes to answer plus the time to wipe Dexie and caches. If the component
// unmounts mid-deletion (route change, full reload, the user backing out
// during a slow network) the promise keeps walking and calls
// `setOwner(null)` and `deleteActiveDb()` against a page that no longer
// knows about it — which is a real production bug (per Fable audit
// 2026-08-22), not just a test artefact: in the wild it leaves the user
// signed out and the local store wiped for a navigation they thought they
// had abandoned. The fix is two parts:
//
//   1. Hold the in-flight promise at module scope.
//   2. Expose `waitForPendingDeletion()` so callers (a component
//      unmounting, a test's `afterEach`, the test runner tearing a
//      worktree down) can wait for the wipe to actually finish before
//      they touch any of the state the wipe mutates.
let inFlight: Promise<void> | null = null;

export function waitForPendingDeletion(): Promise<void> | null {
  return inFlight;
}

/**
 * Test-only escape hatch: drop the in-flight handle without awaiting it.
 * Used by the global `afterEach` in `setupTests.ts` so a test that
 * refuses to resolve (e.g. a deliberately slow fake service) does not
 * deadlock the whole suite. Production code MUST NOT call this.
 */
export function __clearPendingDeletionForTests(): void {
  inFlight = null;
}

export interface DeleteAccountOptions {
  userId: string;
  timeoutMs?: number;
  navigate(path: string, options?: { replace?: boolean }): void;
  sync?: { syncNow(): Promise<void>; dispose(): void };
}

export function performAccountDeletion(opts: DeleteAccountOptions): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = runDeletion(opts).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runDeletion(opts: DeleteAccountOptions): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new Error("offline");
  }

  const sync = opts.sync ?? syncEngine;

  // 1. Best-effort sync so the last local writes are not lost unintentionally.
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, opts.timeoutMs ?? 5000));
  await Promise.race([sync.syncNow().catch(() => undefined), timeout]);

  // 2. Stop the sync engine before the db disappears.
  sync.dispose();

  // 3. Server-side delete (single atomic cascade).
  await getAuthService().deleteAccount();

  // 4. Local cleanup. If any step fails here, the account is already gone on
  //    the server, so we continue and log rather than leaving data behind.
  setOwner(null);
  try {
    await getAuthService().signOut();
  } catch {
    /* ignore */
  }
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("pocketbase_auth");
  }

  try {
    await deleteActiveDb();
  } catch {
    const Dexie = (await import("dexie")).default;
    await Dexie.delete(`calicool-${opts.userId}`);
  }

  if (typeof caches !== "undefined") {
    for (const key of await caches.keys()) {
      if (key.includes(opts.userId)) await caches.delete(key);
    }
  }

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem("account.deleted", "1");
  }
  opts.navigate("/", { replace: true });
}
